import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { quoteRegions } from "../data/quoteRegions";
import { stateOutlines } from "../components/OurLocations";

const outlineById = Object.fromEntries(stateOutlines.map((state) => [state.id, state.d]));

const openings = [
  {
    title: "Traffic Management Planner",
    type: "Full-time",
    state: "New South Wales",
    location: "Sydney, NSW",
    description:
      "Prepare traffic management plans, traffic guidance schemes and associated documentation for a wide range of projects including construction, events and road works.",
  },
  {
    title: "Senior Traffic Engineer",
    type: "Full-time",
    state: "New South Wales",
    location: "Sydney, NSW",
    description:
      "Lead complex traffic engineering projects, mentor junior staff and liaise directly with Transport for NSW, local councils and private clients.",
  },
  {
    title: "Traffic Controller",
    type: "Casual / Part-time",
    state: "New South Wales",
    location: "NSW Wide",
    description:
      "On-site traffic control for construction zones and events. Hold a current Traffic Controller certification and stop/slow bat. Experience preferred but not essential.",
  },
  {
    title: "Project Coordinator",
    type: "Full-time",
    state: "New South Wales",
    location: "Sydney, NSW",
    description:
      "Coordinate project timelines, liaise with clients and internal teams, and ensure plans and permits are submitted and approved on schedule.",
  },
];

const generalRole = {
  title: "General application",
  type: "",
  state: "",
  location: "Australia",
  description:
    "Not sure which role fits? Tell us what you can do and which state you want to work in. We keep good people on file for upcoming jobs.",
};

const roles = [...openings.map((job) => job.title), generalRole.title];
const workTypes = ["Full-time", "Casual / Part-time", "Either"];
const experienceOptions = ["Less than 1 year", "1–2 years", "3–5 years", "5+ years"];
const stateOptions = [
  ...quoteRegions.map((region) => ({ id: region.id, label: region.label })),
  { id: "any", label: "Flexible / any state" },
];

const MAX_RESUME_BYTES = 2 * 1024 * 1024;
const RESUME_EXTENSIONS = [".pdf", ".docx"];

const inputClass = (err) =>
  `w-full rounded-xl border px-4 py-3 text-sm text-black outline-none transition focus:border-transparent focus:ring-2 focus:ring-orange-500 ${
    err ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
  }`;

function careerSubmitUrl() {
  const base = (process.env.REACT_APP_CONTACT_API_URL || "").replace(/\/$/, "");
  return base ? `${base}/api/careers` : "/api/careers";
}

function emptyForm(job) {
  const workType = workTypes.includes(job?.type) ? job.type : "";
  return {
    fullName: "",
    email: "",
    phone: "",
    role: job?.title || generalRole.title,
    workType,
    state: job?.state || "",
    experience: "",
    linkedin: "",
    message: "",
  };
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isAllowedResume(file) {
  const name = file.name.toLowerCase();
  return RESUME_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function StateIcon({ id, className = "h-4 w-4" }) {
  const d = outlineById[id];
  if (!d) return null;
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden>
      <path d={d} fill="currentColor" />
    </svg>
  );
}

function StateSelect({ value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const selected = stateOptions.find((state) => state.label === value);

  useEffect(() => {
    if (!open) return undefined;
    const placeMenu = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    placeMenu();
    const onPointer = (event) => {
      const menu = document.querySelector("[data-state-menu]");
      if (!rootRef.current?.contains(event.target) && !menu?.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopImmediatePropagation();
      setOpen(false);
    };
    const scroller = rootRef.current?.closest("form");
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", placeMenu);
    scroller?.addEventListener("scroll", placeMenu);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", placeMenu);
      scroller?.removeEventListener("scroll", placeMenu);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`${inputClass(error)} flex items-center gap-2 text-left`}
      >
        {selected ? (
          <>
            <StateIcon id={selected.id} className="h-4 w-4 shrink-0 text-orange-500" />
            <span className="min-w-0 flex-1 truncate">{selected.label}</span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate text-gray-400">Where do you want to work?</span>
        )}
        <svg
          className={`h-4 w-4 shrink-0 text-gray-400 transition ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && menuStyle && createPortal(
        <ul
          data-state-menu
          role="listbox"
          aria-label="State"
          style={menuStyle}
          className="fixed z-[90] max-h-64 overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {stateOptions.map((state) => {
            const active = state.label === value;
            return (
              <li key={state.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(state.label);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm ${
                    active ? "bg-orange-50 font-semibold text-orange-600" : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <StateIcon id={state.id} className="h-4 w-4 shrink-0 text-orange-500" />
                  {state.label}
                </button>
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-0.5 text-orange-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function ApplyDialog({ job, onClose }) {
  const [form, setForm] = useState(() => emptyForm(job));
  const [resume, setResume] = useState(null);
  const [resumeError, setResumeError] = useState("");
  const [errors, setErrors] = useState({});
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.querySelector("input, select, textarea, button")?.focus();
    const onKey = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, submitting]);

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const chooseResume = (file) => {
    if (!file) return;
    if (!isAllowedResume(file)) {
      setResume(null);
      setResumeError("Please upload a PDF or DOCX file.");
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      setResume(null);
      setResumeError("That file is larger than 2MB.");
      return;
    }
    setResume(file);
    setResumeError("");
    setErrors((current) => ({ ...current, resume: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Required";
    if (!form.email.trim()) next.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) next.email = "Enter a valid email";
    if (!form.phone.trim()) next.phone = "Required";
    if (!form.role) next.role = "Please select a role";
    if (!form.workType) next.workType = "Please select a work type";
    if (!form.state) next.state = "Please select a state";
    if (!form.experience) next.experience = "Please select your experience";
    if (!form.message.trim()) next.message = "Required";
    if (!resume) next.resume = "Please upload a PDF or DOCX resume";
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const next = validate();
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await fetch(careerSubmitUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          linkedin: form.linkedin.trim(),
          message: form.message.trim(),
          attachments: [
            {
              filename: resume.name,
              content: await fileToBase64(resume),
              contentType: resume.type,
            },
          ],
        }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        /* non-JSON body */
      }
      if (!res.ok) {
        setSubmitError(data.error || "We could not send your application. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setSubmitError("Network error. Check your connection or email info@focustrafficplans.com.au.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-title"
        className="flex max-h-[100dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-3xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-orange-500">Apply</p>
            <h2 id="apply-title" className="mt-1 text-xl font-extrabold text-black">
              {form.role}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-500">
              {([...openings, generalRole].find((item) => item.title === form.role) || job).description}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close application"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-black disabled:opacity-50"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {submitted ? (
          <div className="px-6 py-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-orange-500">
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-black">Application sent</h3>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
              Thanks, {form.fullName.split(" ")[0]}. We have your application for {form.role} and will be in touch if there is a fit.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 rounded-xl bg-orange-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Full name" required error={errors.fullName}>
                <input
                  value={form.fullName}
                  onChange={(event) => setField("fullName", event.target.value)}
                  autoComplete="name"
                  className={inputClass(errors.fullName)}
                />
              </Field>
              <Field label="Phone" required error={errors.phone}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(event) => setField("phone", event.target.value)}
                  autoComplete="tel"
                  className={inputClass(errors.phone)}
                />
              </Field>
            </div>
            <Field label="Email" required error={errors.email}>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setField("email", event.target.value)}
                autoComplete="email"
                className={inputClass(errors.email)}
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Role" required error={errors.role}>
                <select
                  value={form.role}
                  onChange={(event) => setField("role", event.target.value)}
                  className={inputClass(errors.role)}
                >
                  {roles.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </Field>
              <Field label="Work type" required error={errors.workType}>
                <select
                  value={form.workType}
                  onChange={(event) => setField("workType", event.target.value)}
                  className={inputClass(errors.workType)}
                >
                  <option value="">Select...</option>
                  {workTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="State" required error={errors.state}>
                <StateSelect
                  value={form.state}
                  error={errors.state}
                  onChange={(state) => setField("state", state)}
                />
              </Field>
              <Field label="Experience" required error={errors.experience}>
                <select
                  value={form.experience}
                  onChange={(event) => setField("experience", event.target.value)}
                  className={inputClass(errors.experience)}
                >
                  <option value="">Select...</option>
                  {experienceOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="LinkedIn">
              <input
                type="url"
                value={form.linkedin}
                onChange={(event) => setField("linkedin", event.target.value)}
                placeholder="https://linkedin.com/in/your-profile"
                className={inputClass(false)}
              />
            </Field>
            <Field label="Why do you want to join Focus?" required error={errors.message}>
              <textarea
                value={form.message}
                onChange={(event) => setField("message", event.target.value)}
                rows={4}
                placeholder="A short note on your experience and the work you are looking for."
                className={inputClass(errors.message)}
              />
            </Field>
            <Field label="Resume" required error={errors.resume || resumeError}>
              <label
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setDragging(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  chooseResume(event.dataTransfer.files?.[0]);
                }}
                className={`flex cursor-pointer flex-col items-center rounded-xl border border-dashed px-4 py-6 text-center transition ${
                  dragging ? "border-orange-500 bg-orange-50" : "border-gray-300 bg-gray-50 hover:border-orange-400"
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="sr-only"
                  onChange={(event) => {
                    chooseResume(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
                <span className="text-sm font-semibold text-gray-800">Drop your resume here or click to upload</span>
                <span className="mt-1 text-xs text-gray-500">PDF or DOCX, one file, up to 2MB</span>
              </label>
              {resume && (
                <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-800">{resume.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(resume.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setResume(null)}
                    className="shrink-0 text-xs font-semibold text-gray-500 hover:text-orange-500"
                  >
                    Remove
                  </button>
                </div>
              )}
            </Field>
            {submitError && (
              <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center rounded-xl bg-orange-500 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-orange-600 disabled:pointer-events-none disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Submit application"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Careers() {
  const [activeJob, setActiveJob] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-orange-500">
            Join the team
          </p>
          <h1 className="text-3xl font-extrabold tracking-tight text-black sm:text-4xl lg:text-5xl">
            Careers at Focus Traffic Plans
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-gray-500">
            We&apos;re growing fast and always on the lookout for talented,
            driven people who want to make a real impact in traffic
            management across Australia.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="mb-8 text-xl font-bold text-black">Current openings</h2>
        <div className="flex flex-col gap-4">
          {openings.map((job) => (
            <div
              key={job.title}
              className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:border-orange-400/60 hover:shadow-md"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-[15px] font-bold text-black">{job.title}</h3>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    <span className="inline-flex items-center rounded-full bg-orange-50 px-3 py-0.5 text-[11px] font-semibold text-orange-600 ring-1 ring-orange-500/20">
                      {job.type}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-0.5 text-[11px] font-semibold text-gray-500">
                      {job.location}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-gray-500">{job.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveJob(job)}
                  className="shrink-0 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                >
                  Apply now
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
          <p className="text-sm font-medium text-gray-700">Don&apos;t see a role that fits?</p>
          <p className="mt-1 text-sm text-gray-500">We&apos;re always interested in hearing from talented people.</p>
          <button
            type="button"
            onClick={() => setActiveJob(generalRole)}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800"
          >
            Send a general application
          </button>
        </div>
      </div>

      {activeJob && <ApplyDialog key={activeJob.title} job={activeJob} onClose={() => setActiveJob(null)} />}
    </div>
  );
}
